import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Loader2, RotateCcw, Info } from 'lucide-react';
import { getMerchants, manualQrSettlement } from '../../services/adminApi';
import { showLoadingToast, updateToast, showErrorToast } from '../../utils/toast';

const SearchableMerchantSelect = ({ merchants, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filter to include only ACTIVE merchants, then match search term
  const activeMerchants = merchants.filter(m => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
  const filtered = activeMerchants.filter(m => 
    (m.merchantName || m.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.merchantId || m.id || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedMerchant = activeMerchants.find(m => (m.merchantId || m.id) === value);
  const displayValue = value === 'all' 
    ? 'All Merchants (Bulk Settlement)' 
    : selectedMerchant 
      ? `${selectedMerchant.merchantName || selectedMerchant.name} (${selectedMerchant.merchantId || selectedMerchant.id})`
      : 'Select merchant (Optional)';

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.searchable-dropdown-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  return (
    <div className="relative searchable-dropdown-container w-full">
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
      >
        <span className="truncate">{displayValue}</span>
        <span className="text-gray-400 text-xs">▼</span>
      </div>

      {isOpen && (
        <div className="absolute z-50 min-w-[8rem] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80 mt-1 bg-white">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              placeholder="Search active merchant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto p-1">
            <div
              onClick={() => {
                onChange('all');
                setIsOpen(false);
                setSearch('');
              }}
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-slate-100 hover:text-accent-foreground"
            >
              All Merchants (Bulk Settlement)
            </div>
            {filtered.length === 0 ? (
              <div className="py-2 px-2 text-xs text-gray-500 text-center">No active merchants found</div>
            ) : (
              filtered.map(m => (
                <div
                  key={m.id || m.merchantId}
                  onClick={() => {
                    onChange(m.merchantId || m.id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-slate-100 hover:text-accent-foreground"
                >
                  {m.merchantName || m.name} ({m.merchantId || m.id})
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const QrSettlement = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    merchantId: 'all',
    cutoffDateTime: ''
  });

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      const response = await getMerchants(1, 1000, 'all');
      
      let merchantList = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          merchantList = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          merchantList = response.data.content;
        }
      }
      setMerchants(merchantList);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      showErrorToast('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    const toastId = showLoadingToast('Triggering settlement process...');

    try {
      const params = {};
      if (formData.merchantId && formData.merchantId !== 'all') {
        params.merchantId = formData.merchantId;
      }
      
      if (formData.cutoffDateTime) {
        // Ensure format is correct for backend (YYYY-MM-DDTHH:mm:ss)
        const dateStr = formData.cutoffDateTime.length === 16 
          ? `${formData.cutoffDateTime}:00` 
          : formData.cutoffDateTime;
        params.cutoffDateTime = dateStr;
      }

      await manualQrSettlement(params);
      
      updateToast(toastId, 'Manual settlement triggered successfully!', 'success');
      
      // Reset cutoff date but keep merchant if desired, or reset both
      setFormData({
        merchantId: 'all',
        cutoffDateTime: ''
      });
    } catch (error) {
      console.error('Failed to trigger settlement:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to trigger settlement';
      updateToast(toastId, errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && merchants.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manual QR Settlement</h1>
          <p className="text-gray-500 mt-1">Settle pending QR transactions to merchant wallets</p>
        </div>
      </div>

      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-4 border-b bg-white">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-blue-600" />
            Settlement Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Target Merchant</Label>
                <SearchableMerchantSelect 
                  merchants={merchants}
                  value={formData.merchantId} 
                  onChange={(val) => setFormData({...formData, merchantId: val})}
                />
                <p className="text-xs text-gray-500">Leave as 'All Merchants' to process settlement for the entire system.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Cutoff Date & Time (Optional)</Label>
                <Input 
                  type="datetime-local" 
                  value={formData.cutoffDateTime} 
                  onChange={(e) => setFormData({...formData, cutoffDateTime: e.target.value})} 
                  className="bg-white"
                />
                <p className="text-xs text-gray-500">Only transactions created BEFORE this time will be settled.</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
              <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">Important Note:</p>
                <p className="mt-1">
                  Manual settlement will process all 'SUCCESS' QR transactions that haven't been settled yet. 
                  The funds will be moved to the merchant's main wallet balance.
                </p>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-base font-semibold transition-colors shadow-sm"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing Settlement...
                </>
              ) : (
                'Run Settlement Process Now'
              )}
            </Button>

          </form>
        </CardContent>
      </Card>
      
    </div>
  );
};

export default QrSettlement;
